import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'kgr-code-input',
  templateUrl: './code-input.component.html',
  styleUrls: ['./code-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CodeInputComponent,
      multi: true,
    },
  ],
})
export class CodeInputComponent implements ControlValueAccessor, OnChanges {
  @Input() length: number = 6;
  @Input() alphanumeric: boolean = false;
  @ViewChildren('digitInput') digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  isDisabled = false;

  private onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private cd: ChangeDetectorRef) {
    this.digits = Array(this.length).fill('');
  }

  ngOnChanges(): void {
    this.digits = Array(this.length).fill('');
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const sanitized = this.alphanumeric ? input.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() : input.value.replace(/\D/g, '');

    if (!sanitized) {
      this.digits[index] = '';
      this.emitChange();
      return;
    }

    this.digits[index] = sanitized[0];
    input.value = sanitized[0];
    this.emitChange();

    if (index < this.length - 1) {
      this.focusInput(index + 1);
    }
  }

  onFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.select());
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = '';
        this.emitChange();
      } else if (index > 0) {
        this.digits[index - 1] = '';
        this.emitChange();
        this.focusInput(index - 1);
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const pasted = (this.alphanumeric ? text.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() : text.replace(/\D/g, ''))
      .split('')
      .slice(0, this.length);

    this.digits = Array(this.length).fill('');
    pasted.forEach((d, i) => {
      this.digits[i] = d;
    });

    this.emitChange();

    const nextEmpty = this.digits.findIndex((d) => !d);
    this.focusInput(nextEmpty === -1 ? this.length - 1 : nextEmpty);
  }

  private focusInput(index: number): void {
    setTimeout(() => {
      const inputs = this.digitInputs.toArray();
      inputs[index]?.nativeElement.focus();
      inputs[index]?.nativeElement.select();
    });
  }

  private emitChange(): void {
    this.onChange(this.digits.join(''));
    this.cd.markForCheck();
  }

  writeValue(value: string): void {
    this.digits = Array(this.length).fill('');
    if (value) {
      value
        .split('')
        .slice(0, this.length)
        .forEach((c, i) => {
          this.digits[i] = c;
        });
    }
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }
}
